import { Camera, Matrix4, Object3D, Scene, Vector2, Vector3 } from "three";

export class CSS2DObject extends Object3D {
  isCSS2DObject: boolean;
  element: HTMLDivElement;
  center: Vector2;
  angle: string = "0deg";
  scaleAlpha: number = 1;

  constructor(element = document.createElement("div")) {
    super();

    this.isCSS2DObject = true;

    this.element = element;

    this.element.style.position = "absolute";
    this.element.style.userSelect = "none";

    this.element.setAttribute("draggable", "false");

    this.center = new Vector2(0.5, 0.5); // ( 0, 0 ) is the lower left; ( 1, 1 ) is the top right
  }

  copy(source: any, recursive: boolean) {
    super.copy(source, recursive);

    this.element = source.element.cloneNode(true);

    this.center = source.center;

    return this;
  }
}

export type CSS2DParameters = {
  element?: HTMLElement;
};

//

const _vector = new Vector3();
const _viewMatrix = new Matrix4();
const _viewProjectionMatrix = new Matrix4();
const _a = new Vector3();
const _b = new Vector3();

export class CSS2DRenderer {
  domElement: HTMLElement;

  private _width: number = 0;
  private _height: number = 0;
  private _widthHalf: number = 0;
  private _heightHalf: number = 0;
  private cache: any = {
    objects: new WeakMap(),
  };

  constructor(parameters?: CSS2DParameters) {
    this.domElement =
      parameters?.element !== undefined
        ? parameters?.element
        : document.createElement("div");
    this.domElement.style.overflow = "hidden";
  }

  getSize(): any {
    return {
      width: this._width,
      height: this._height,
    };
  }

  setSize(width: number, height: number) {
    this._width = width;
    this._height = height;

    this._widthHalf = this._width / 2;
    this._heightHalf = this._height / 2;

    this.domElement.style.width = width + "px";
    this.domElement.style.height = height + "px";
  }

  render(scene: Scene, camera: Camera): void {
    this.domElement.innerHTML = "";
    if (scene.matrixWorldAutoUpdate === true) scene.updateMatrixWorld();
    if (camera.parent === null && camera.matrixWorldAutoUpdate === true)
      camera.updateMatrixWorld();

    _viewMatrix.copy(camera.matrixWorldInverse);
    _viewProjectionMatrix.multiplyMatrices(
      camera.projectionMatrix,
      _viewMatrix
    );

    this.renderObject(scene, scene, camera);
    this.zOrder(scene);
  }

  renderObject(object: any, scene: Scene, camera: Camera) {
    let objectData;

    if (object.isCSS2DObject) {
      _vector.setFromMatrixPosition(object.matrixWorld);
      _vector.applyMatrix4(_viewProjectionMatrix);

      const visible =
        object.visible === true &&
        _vector.z >= -1 &&
        _vector.z <= 1 &&
        object.layers.test(camera.layers) === true;
      object.element.style.display = visible === true ? "" : "none";

      if (visible === true) {
        object.onBeforeRender(this, scene, camera);

        const element = object.element;

        element.style.transform =
          "translate(" +
          -100 * object.center.x +
          "%," +
          -100 * object.center.y +
          "%)" +
          "translate(" +
          (_vector.x * this._widthHalf + this._widthHalf) +
          "px," +
          (-_vector.y * this._heightHalf + this._heightHalf) +
          "px)" +
          "rotate(" + object.angle + ")" +
          "scale(" + object.scaleAlpha + "," + object.scaleAlpha + ")";

        if (element.parentNode !== this.domElement) {
          this.domElement.appendChild(element);
        }

        object.onAfterRender(this, scene, camera);
      }
      objectData = {
        distanceToCameraSquared: this.getDistanceToSquared(camera, object),
      };
    } else {
      objectData = {};
    }

    this.cache.objects.set(object, objectData);

    for (let i = 0, l = object.children.length; i < l; i++) {
      this.renderObject(object.children[i], scene, camera);
    }
  }

  getDistanceToSquared(object1: Object3D, object2: Object3D) {
    _a.setFromMatrixPosition(object1.matrixWorld);
    _b.setFromMatrixPosition(object2.matrixWorld);

    return _a.distanceToSquared(_b);
  }

  filterAndFlatten(scene: Scene) {
    const result: CSS2DObject[] = [];

    scene.traverse((object: any) => {
      if (object.isCSS2DObject) result.push(object);
    });

    return result;
  }

  zOrder(scene: Scene) {
    const sorted = this.filterAndFlatten(scene).sort((a, b) => {
      if (a.renderOrder !== b.renderOrder) {
        return b.renderOrder - a.renderOrder;
      }

      const distanceA = this.cache.objects.get(a).distanceToCameraSquared;
      const distanceB = this.cache.objects.get(b).distanceToCameraSquared;

      return distanceA - distanceB;
    });

    const zMax = sorted.length;

    for (let i = 0, l = sorted.length; i < l; i++) {
      sorted[i].element.style.zIndex = `${zMax - i}`;
    }
  }
}
